import { useState } from "react"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import type { ProductVisualBrowseImage, StoreCode } from "@nova-ai/shared"
import { AlertCircle, Images } from "lucide-react"

import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { ProductVisualImagesSkeleton } from "./product-visual-images-skeleton"
import { ProductVisualLightbox, type EnlargedProductVisual } from "./product-visual-lightbox"
import { ProductVisualReviewDetails } from "./product-visual-review-details"
import { ProductVisualStudioCard } from "./product-visual-studio-card"

// Studio is a read-only review surface. Artifact provenance and template
// freshness describe the exact published image; no write action belongs here.
export function ProductVisualImagesGrid({
  store,
  visualKind,
  images,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  error,
  onLoadMore,
}: {
  store: StoreCode
  visualKind: ProductVisualKind
  images: ProductVisualBrowseImage[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  onLoadMore: () => Promise<unknown>
}) {
  const [selected, setSelected] = useState<{
    item: ProductVisualBrowseImage
    image: EnlargedProductVisual
  } | null>(null)

  return (
    <section className="space-y-5">
      <ProductVisualImagesBody
        images={images}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        error={error}
        onLoadMore={onLoadMore}
        store={store}
        visualKind={visualKind}
        onSelect={(item, image) => setSelected({ item, image })}
      />

      {selected ? (
        <ProductVisualLightbox
          image={selected.image}
          details={
            <ProductVisualReviewDetails
              image={selected.item}
              store={store}
              visualKind={visualKind}
            />
          }
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  )
}

function ProductVisualImagesBody({
  images,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  error,
  onLoadMore,
  store,
  visualKind,
  onSelect,
}: {
  images: ProductVisualBrowseImage[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  onLoadMore: () => Promise<unknown>
  store: StoreCode
  visualKind: ProductVisualKind
  onSelect: (item: ProductVisualBrowseImage, image: EnlargedProductVisual) => void
}) {
  if (isLoading) {
    return <ProductVisualImagesSkeleton />
  }
  if (error && images.length === 0) {
    return (
      <Callout
        tone="destructive"
        icon={<AlertCircle className="size-4" />}
        title="Images failed to load"
      >
        <p className="mt-2 text-sm text-destructive">{error.message}</p>
      </Callout>
    )
  }
  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
        <Images className="size-5" aria-hidden="true" />

        <span>No generated images yet for this store and visual kind.</span>

        <span className="text-xs">Published images will appear here automatically.</span>
      </div>
    )
  }
  return (
    <>
      <ul
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        aria-busy={isFetchingNextPage}
      >
        {images.map((image) => (
          <li key={`${image.simpleSku}:${image.cell.currentFingerprint}`}>
            <ProductVisualStudioCard
              item={image}
              store={store}
              visualKind={visualKind}
              onReview={onSelect}
            />
          </li>
        ))}
      </ul>

      {error ? (
        <Callout tone="destructive" dense title="More images failed to load">
          <p className="mt-1 text-xs text-destructive">{error.message}</p>
        </Callout>
      ) : null}

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button disabled={isFetchingNextPage} onClick={() => void onLoadMore()}>
            {isFetchingNextPage ? "Loading more…" : "Load more"}
          </Button>
        </div>
      ) : null}
    </>
  )
}
